/** A node's kind. Sidecars sit outside the reporting rollup; board members sit beside the top seat, not above it. */
export type OrgChartNodeKind = 'seat' | 'sidecar' | 'board';

/** Per-item ids so an edited responsibility is distinguishable from a delete plus an add when forks are merged. */
export interface Responsibility {
  id: string;
  text: string;
}

export interface OrgChart {
  id: string;
  parentChartId: string | null;
  teamId: string;
  name: string;
  status: 'active' | 'archived';
  /** The date this fork is planning toward, distinct from when it last changed. */
  targetDate: string | null;
  updatedAt: string;
}

export interface OrgChartSeat {
  id: string;
  chartId: string;
  /**
   * Identity shared by the equivalent seat in every fork. Compared with `===`;
   * there is no chain to walk and nothing to maintain.
   */
  lineageId: string;
  /** Addressed by lineage so the value is identical across forks and a re-parent merges as a plain field copy. */
  parentLineageId: string | null;
  nodeKind: OrgChartNodeKind;
  title: string;
  ownerId: string | null;
  responsibilities: Responsibility[];
  sortOrder: number;
  tags: string[];
  /** Ideal DISC Natural scores for role-fit analysis. Null/undefined = AI-inferred when needed. */
  idealDiscScores?: { d: number; i: number; s: number; c: number } | null;
}

/** What `save_org_chart` reports back, so the caller can log audit entries for what actually changed. */
export interface SaveResult {
  updatedAt: string;
  created: { id: string; lineageId: string; title: string }[];
  updated: { id: string; lineageId: string; title: string; changed: string[] | null }[];
  deleted: { id: string; lineageId: string; title: string }[];
}

/** Which way a merge runs, named from the fork's point of view. */
export type MergeDirection = 'push' | 'pull';

/** One seat's worth of a merge request: omit `fields` to take the whole seat. */
export interface MergeRequest {
  lineageId: string;
  fields?: string[];
  action?: 'merge' | 'delete' | 'restore';
  /** Include source-side descendants that are absent from the receiving chart. */
  descendants?: boolean;
}

/** What `apply_org_chart_merge` reports back. Mirrors `SaveResult` so the audit path is shared. */
export interface MergeApplyResult {
  targetUpdatedAt: string;
  sourceUpdatedAt: string;
  pairUpdatedAt: string | null;
  applied: { id: string; lineageId: string; title: string; changed: string[]; added: boolean; deleted: boolean }[];
  skipped: { lineageId: string; reason: string }[];
}

export interface LinkLineagesResult {
  displayedRowId: string;
  displayedTitle: string;
  linkedRowId: string;
  linkedTitle: string;
  oldLineageId: string;
  newLineageId: string;
  affectedCharts: number;
  displayedUpdatedAt: string;
  otherUpdatedAt: string;
}

/**
 * Phase F (#219): a team's standing authority over one seat subtree of one
 * chart. Belongs to the team, not a user — every current manager of
 * `teamId` inherits it, and losing manager rank removes it immediately.
 */
export interface OrgChartSubtreeGrant {
  id: string;
  chartId: string;
  rootLineageId: string;
  teamId: string;
  createdBy: string;
  createdAt: string;
}

export const isTrunk = (chart: OrgChart) => chart.parentChartId === null;
